import React from 'react';
import { Link } from 'react-router-dom';
import { distributorInfo } from '../../mockData';

const SUPPORTERS = [
  { src: '/paih-logo-latest.png', name: 'Polish Investment & Trade Agency', role: 'Trade Partner · Manila', href: 'https://www.paih.gov.pl/en/', h: 'h-8' },
  { src: '/medical-inventi-logo.png', name: 'Medical Inventi S.A.', role: 'Manufacturer · Poland', href: 'https://medicalinventi.pl/', h: 'h-9' },
];

const SectionLabel = ({ children }) => (
  <h4 className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500 mb-6">
    {children}
  </h4>
);

const NavLink = ({ to, href, children, ...rest }) => {
  const cls =
    'group relative inline-block text-sm text-slate-300 transition-colors duration-300 hover:text-white';
  const underline = (
    <span className="absolute left-0 -bottom-1 h-px w-0 bg-blue-400 transition-all duration-300 group-hover:w-full" />
  );
  if (to) {
    return (
      <Link to={to} className={cls} {...rest}>
        {children}
        {underline}
      </Link>
    );
  }
  return (
    <a href={href} className={cls} {...rest}>
      {children}
      {underline}
    </a>
  );
};

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer
      id="footer"
      data-testid="footer-section"
      className="relative overflow-hidden bg-[#05070d] text-white"
    >
      {/* Top accent hairline — FMSi orange → royal blue */}
      <div className="h-px w-full bg-gradient-to-r from-[#E87A20] via-blue-500/40 to-transparent" />
      {/* Ambient glow */}
      <div className="pointer-events-none absolute -top-24 right-0 h-72 w-72 rounded-full bg-blue-600/10 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 left-1/4 h-64 w-64 rounded-full bg-[#E87A20]/5 blur-[120px]" />

      <div className="relative container mx-auto px-6 py-20">
        <div className="grid gap-14 md:grid-cols-2 lg:grid-cols-12">
          {/* Brand */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-4 mb-7">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-white/95 p-2 shadow-[0_8px_30px_rgba(0,0,0,0.5)] ring-1 ring-white/10">
                <img
                  src="/flexioss-logo.png"
                  alt="FlexiOss bone regenerative biomaterial logo"
                  className="h-full w-full object-contain"
                />
              </div>
              <div>
                <h3 className="text-xl font-bold tracking-tight">FlexiOss®</h3>
                <p className="text-[11px] uppercase tracking-[0.2em] text-blue-400/80">
                  Bone Regeneration
                </p>
              </div>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-slate-400">
              Next-generation implantable biomaterial engineered for advanced bone
              defect filling and regenerative medicine.
            </p>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <SectionLabel>Explore</SectionLabel>
            <ul className="space-y-4">
              {[
                { label: 'Innovative Properties', to: '/#innovation' },
                { label: 'Clinical Evidence', to: '/#research' },
                { label: 'Our Products', to: '/#products' },
                { label: 'Contact Us', to: '/#footer' },
              ].map((l) => (
                <li key={l.label}>
                  <NavLink to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    {l.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div className="lg:col-span-2">
            <SectionLabel>Products</SectionLabel>
            <ul className="space-y-4">
              <li><NavLink to="/flexioss-ortho" data-testid="footer-link-ortho">FlexiOss® Ortho</NavLink></li>
              <li><NavLink to="/flexioss-dent" data-testid="footer-link-dent">FlexiOss® Dent</NavLink></li>
              <li><NavLink to="/flexioss-vet" data-testid="footer-link-vet">FlexiOss® Vet</NavLink></li>
              <li><NavLink to="/faq" data-testid="footer-link-faq">Clinical FAQ</NavLink></li>
            </ul>
          </div>

          {/* Contact — editorial, label-driven */}
          <div className="lg:col-span-4">
            <SectionLabel>Get in Touch</SectionLabel>
            <div className="space-y-7 border-l border-white/10 pl-6">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Email</p>
                <a
                  href={`mailto:${distributorInfo.contact.email}`}
                  data-testid="footer-email"
                  className="text-sm text-slate-200 transition-colors hover:text-blue-400"
                >
                  {distributorInfo.contact.email}
                </a>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Direct Lines</p>
                <ul className="space-y-1.5">
                  {distributorInfo.contact.phones.map((phone, i) => (
                    <li key={i} className="flex items-baseline gap-2 text-sm">
                      <span className="w-12 shrink-0 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{phone.type}</span>
                      <a
                        href={`tel:${phone.number}`}
                        data-testid={`footer-phone-${i}`}
                        className="text-slate-200 transition-colors hover:text-blue-400"
                      >
                        {phone.number}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Main Office</p>
                <p data-testid="footer-address-main" className="text-sm leading-relaxed text-slate-300">
                  {distributorInfo.contact.mainAddress}
                </p>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Satellite Office</p>
                <p data-testid="footer-address-satellite" className="text-sm leading-relaxed text-slate-300">
                  {distributorInfo.contact.satelliteAddress}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Partners — bespoke, cohesive lockup */}
        <div className="mt-16 border-t border-white/10 pt-12">
          <div className="grid gap-12 lg:grid-cols-12 lg:items-end">
            {/* Exclusive Distributor — FMSi */}
            <div className="lg:col-span-5">
              <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">
                Exclusive Distributor
              </p>
              <a
                href="https://www.fmsmedequip.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-fmsi-link"
                className="group relative inline-flex flex-col items-start overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.06] to-transparent px-6 py-5 ring-1 ring-inset ring-white/5 transition-all duration-300 hover:border-[#E87A20]/40"
              >
                <span className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full bg-[#E87A20]/15 opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-100" />
                <span className="pointer-events-none absolute left-0 top-0 h-px w-full bg-gradient-to-r from-[#E87A20]/60 to-transparent" />
                <img
                  src="/fmsi-logo-full.png"
                  alt="FMSi — Fortschritt Medical Supplies Inc., exclusive FlexiOss distributor in the Philippines"
                  className="relative h-14 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.03]"
                />
                <span className="relative mt-4 inline-flex items-center gap-1.5 rounded-full border border-[#E87A20]/30 bg-[#E87A20]/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-[#E87A20]">
                  Exclusive Distributor · Philippines
                </span>
              </a>
            </div>

            {/* Supported By — matching bespoke glass cards */}
            <div className="lg:col-span-7">
              <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500 lg:text-right">
                Endorsed &amp; Supported By
              </p>
              <div className="flex flex-wrap gap-4 lg:justify-end">
                {SUPPORTERS.map((s) => (
                  <a
                    key={s.name}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-supporter-${s.name.toLowerCase().split(' ')[0]}`}
                    className="group relative flex min-w-[210px] flex-1 items-center gap-4 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4 ring-1 ring-inset ring-white/5 transition-all duration-300 hover:border-blue-400/40 hover:bg-white/[0.07]"
                  >
                    <span className="pointer-events-none absolute -left-8 -top-8 h-20 w-20 rounded-full bg-blue-500/15 opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-100" />
                    <img
                      src={s.src}
                      alt={`${s.name} — partner supporting FlexiOss in the Philippines`}
                      loading="lazy"
                      className={`relative ${s.h} w-auto flex-shrink-0 object-contain opacity-70 grayscale transition-all duration-300 group-hover:opacity-100 group-hover:grayscale-0`}
                    />
                    <span className="relative border-l border-white/15 pl-4">
                      <span className="block text-xs font-bold text-white">{s.name}</span>
                      <span className="mt-0.5 block text-[10px] uppercase tracking-[0.16em] text-slate-500">{s.role}</span>
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-14 flex flex-col items-center justify-between gap-5 border-t border-white/10 pt-8 md:flex-row">
          <p className="text-sm text-slate-500">
            © {currentYear} FlexiOss®. All rights reserved. Distributed by{' '}
            <a
              href="https://www.fmsmedequip.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-[#E87A20] transition-colors hover:text-[#f09040]"
            >
              {distributorInfo.name}
            </a>
            .
          </p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <Link to="/territory-insights" data-testid="footer-link-coverage" className="text-xs uppercase tracking-wider text-slate-500 transition-colors hover:text-[#E87A20]">Coverage Map</Link>
            <Link to="/privacy-policy" className="text-xs uppercase tracking-wider text-slate-500 transition-colors hover:text-slate-200">Privacy</Link>
            <Link to="/cookie-policy" className="text-xs uppercase tracking-wider text-slate-500 transition-colors hover:text-slate-200">Cookies</Link>
            <Link to="/terms-of-service" className="text-xs uppercase tracking-wider text-slate-500 transition-colors hover:text-slate-200">Terms</Link>
            <Link to="/non-disclosure" className="text-xs uppercase tracking-wider text-slate-500 transition-colors hover:text-slate-200">NDA</Link>
            <Link to="/medical-disclaimer" className="text-xs uppercase tracking-wider text-slate-500 transition-colors hover:text-slate-200">Disclaimer</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
